import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormCheckboxGroup } from '@/components/forms/form-checkbox-group';
import { FormInputField } from '@/components/forms/form-input-field';
import { useToast } from '@/components/ui/hooks/use-toast';
import { LoaderIcon } from '@/components/ui/loader-icon';
import { createAssessment, getAssessmentContents, getAssessments, updateAssessment } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { AssessmentWithContent } from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ReactNode } from 'react';
import { z } from 'zod';

interface CreateEditAssessmentProps {
  trigger: ReactNode;
  from?: AssessmentWithContent;
}

const formSchema = z.object({
  name: z.string(),
  displayName: z.string().nullable(),
  contentIds: z.array(z.string()),
});

export const CreateEditAssessment = ({ trigger, from }: CreateEditAssessmentProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: assessmentContents = [], isPending } = useQuery(getAssessmentContents());
  const { mutateAsync: createAssessmentMutate } = useMutation({
    mutationFn: createAssessment,
  });

  const { mutateAsync: updateAssessmentMutate } = useMutation({
    mutationFn: updateAssessment,
  });

  const onSubmit = ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const apiCall = from
      ? () => updateAssessmentMutate({ id: from.id, data: formData })
      : () => createAssessmentMutate(formData);
    return apiCall()
      .then(() => {
        toast({
          title: `${from ? 'Edited' : 'Created'} assessment successfully`,
        });
        queryClient.invalidateQueries(getAssessments());
        closeModal();
      })
      .catch((e) => {
        toast({
          title: `Error while ${from ? 'editing' : 'creating'} assessment`,
          description: airServerErrorFrom(e)?.userFacingMessage || '',
        });
      });
  };

  return (
    <FormModal
      modalTitle={`${from ? 'Edit' : 'Create'} assessment`}
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      defaultValues={{
        name: from?.name || '',
        displayName: from?.displayName || undefined,
        contentIds: from?.contents.map(({ id }) => id) || [],
      }}
    >
      {({ form }) => {
        return (
          <div className="grid gap-4 py-4">
            {from && (
              <div className="w-full">
                Editing assessment will NOT affect sessions that have already created their feedback forms. We create
                all session feedback forms one day before the session starts.
              </div>
            )}
            <FormInputField
              form={form}
              name="name"
              label="Assessment name"
              inputProps={{ placeholder: 'Assessment name', disabled: true }}
            />
            <FormInputField
              form={form}
              name="displayName"
              label="Display name (optional)"
              inputProps={{ placeholder: 'Display name' }}
            />
            <div className="w-full">
              {isPending ? (
                <LoaderIcon />
              ) : (
                <FormCheckboxGroup
                  label="Assessment contents"
                  form={form}
                  name="contentIds"
                  options={assessmentContents.map(({ id }) => id)}
                  optionLabel={(id) => assessmentContents.find(({ id: assessmentId }) => assessmentId === id)?.name}
                />
              )}
            </div>
          </div>
        );
      }}
    </FormModal>
  );
};
